import salesService from '../services/salesService';
import { toast } from 'react-hot-toast';

/**
 * Format phone number for WhatsApp
 * WhatsApp requires: country code + number (without + sign in URL)
 * Example: "+91 9600 635 244" -> "919600635244"
 * Example: "9600635211" -> "919600635211" (adds India country code 91)
 * Example: "09600635211" -> "919600635211" (removes leading 0, adds country code)
 * 
 * @param {string} phone - Phone number to format
 * @returns {string|null} - Formatted phone number or null if invalid
 */
export const formatPhoneNumberForWhatsApp = (phone) => {
  if (!phone || typeof phone !== 'string') {
    return null;
  }
  
  // Remove all non-digit characters except +
  let formatted = phone.replace(/[^\d+]/g, '').trim();
  
  if (formatted.length === 0) {
    return null;
  }
  
  // Remove + sign if present (WhatsApp URL doesn't need it)
  formatted = formatted.replace(/\+/g, '');
  
  // If empty after removing +, return null
  if (formatted.length === 0) {
    return null;
  }
  
  // Validate: should only contain digits
  if (!/^\d+$/.test(formatted)) {
    console.warn('Phone number contains non-digits:', formatted);
    return null;
  }
  
  // Handle Indian phone numbers (most common case)
  // If number starts with 0, remove it (local format)
  if (formatted.startsWith('0')) {
    formatted = formatted.substring(1);
  }
  
  // If number is 10 digits and doesn't start with country code, assume it's Indian and add 91
  if (formatted.length === 10 && !formatted.startsWith('91')) {
    formatted = '91' + formatted;
  }
  
  // If number is 11 digits and starts with 0, remove 0 and add 91
  if (formatted.length === 11 && formatted.startsWith('0')) {
    formatted = '91' + formatted.substring(1);
  }
  
  // Validate: should be at least 10 digits (after formatting)
  if (formatted.length < 10) {
    console.warn('Phone number too short after formatting:', formatted);
    return null;
  }
  
  // Validate: should not be too long (max 15 digits for international numbers)
  if (formatted.length > 15) {
    console.warn('Phone number too long:', formatted);
    return null;
  }
  
  console.log('Original phone:', phone, '-> Formatted for WhatsApp:', formatted);
  return formatted;
};

/**
 * Share bill to WhatsApp
 * Generates PDF receipt and opens WhatsApp with the bill
 * 
 * @param {number|string} orderId - Sales order ID
 * @param {Object} customer - Customer object with phone property (optional)
 * @param {string} orderNumber - Order number for the message (optional)
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
export const shareBillToWhatsApp = async (orderId, customer = null, orderNumber = null) => {
  try {
    // Show loading toast
    const loadingToast = toast.loading('Generating bill...');
    
    // Generate PDF receipt
    const receiptBlob = await salesService.generateReceipt(orderId, 'CUSTOMER', null);
    
    // Dismiss loading toast
    toast.dismiss(loadingToast);
    
    if (!receiptBlob) {
      toast.error('Failed to generate receipt');
      return false;
    }
    
    const fileName = `Invoice-${orderNumber || orderId}.pdf`;
    const file = new File([receiptBlob], fileName, { type: 'application/pdf' });
    const customerPhone = customer?.phone ? formatPhoneNumberForWhatsApp(customer.phone) : null;
    const messageText = `Invoice ${orderNumber || orderId}`;
    const message = encodeURIComponent(messageText);

    // Try Web Share API first (works best on mobile and some desktop browsers)
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        // If customer phone is available, open WhatsApp chat first
        if (customerPhone) {
          const whatsappUrl = `https://wa.me/${customerPhone}?text=${message}`;
          window.open(whatsappUrl, '_blank');
          // Wait a bit for WhatsApp to open
          await new Promise((r) => setTimeout(r, 1000));
        }
        
        // Share the file using Web Share API
        await navigator.share({
          title: messageText,
          text: messageText,
          files: [file]
        });
        
        toast.success('Bill shared successfully!');
        return true;
      } catch (shareError) {
        // If share fails, continue with fallback method
        console.log('Web Share API failed, falling back to download method:', shareError);
      }
    }
    
    // Fallback: Download PDF and open WhatsApp
    // Create download link
    const blobUrl = window.URL.createObjectURL(receiptBlob);
    const downloadLink = document.createElement('a');
    downloadLink.href = blobUrl;
    downloadLink.download = fileName;
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    
    // Download the PDF
    downloadLink.click();
    
    // Clean up after a short delay
    setTimeout(() => {
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);
    
    // Copy message to clipboard for easy pasting
    try {
      await navigator.clipboard.writeText(messageText);
    } catch (err) {
      console.log('Could not copy to clipboard:', err);
    }
    
    // Open WhatsApp with customer's chat if phone number is available
    let whatsappUrl;
    if (customerPhone) {
      // Open WhatsApp directly to customer's chat
      whatsappUrl = `https://wa.me/${customerPhone}?text=${message}`;
      toast.success(`Opening WhatsApp chat for customer. PDF downloaded - please attach it.`, {
        duration: 5000,
      });
    } else {
      // Open WhatsApp Web for user to search manually
      whatsappUrl = `https://web.whatsapp.com/send?text=${message}`;
      toast.success('PDF downloaded. Opening WhatsApp - please attach the PDF file.', {
        duration: 5000,
      });
    }
    
    // Open WhatsApp in new tab/window
    window.open(whatsappUrl, '_blank');
    
    return true;
  } catch (error) {
    console.error('Error sharing bill to WhatsApp:', error);
    toast.error('Failed to share bill to WhatsApp. Please try again.');
    return false;
  }
};

/**
 * Share bill to WhatsApp (alternative method using WhatsApp Web API)
 * This method tries to use WhatsApp Web's share feature
 * 
 * @param {number|string} orderId - Sales order ID
 * @param {Object} customer - Customer object with phone property (optional)
 * @param {string} orderNumber - Order number for the message (optional)
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
export const shareBillToWhatsAppWeb = async (orderId, customer = null, orderNumber = null) => {
  try {
    return await shareBillToWhatsApp(orderId, customer, orderNumber);
  } catch (error) {
    console.error('Error sharing bill to WhatsApp Web:', error);
    toast.error('Failed to share bill to WhatsApp. Please try again.');
    return false;
  }
};

export default {
  formatPhoneNumberForWhatsApp,
  shareBillToWhatsApp,
  shareBillToWhatsAppWeb
};
